import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { api, errorMessage } from "../lib/api";

export default function ResetPasswordPage() {
  const { token = "" } = useParams();
  const { user, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState<{ name: string; email: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(errorMessage(e)));
  }, [token]);

  if (user && !info) return <Navigate to="/cellar" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("The two passwords don't match"); return; }
    setBusy(true);
    try {
      await resetPassword(token, password);
      toast.success("Password updated — you're signed in");
      navigate("/cellar", { replace: true });
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <div className="grain flex min-h-screen items-center px-8 py-16 lg:px-24" data-testid="reset-page">
      <div className="w-full max-w-md rise">
        <img src="/brand/logo.png" alt="Pierre Hourlier Wines" className="h-24 w-auto" data-testid="auth-logo" />
        {error && (
          <div data-testid="reset-error">
            <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">This link has expired</h1>
            <p className="mt-4 text-sm text-neutral-600">{error}</p>
            <Button variant="outline" className="mt-8 rounded-none" onClick={() => navigate("/login?forgot=1")} data-testid="reset-request-again">Request a new link</Button>
          </div>
        )}
        {!error && !info && <p className="mt-6 font-serif text-2xl text-neutral-500" data-testid="reset-loading">Checking your link…</p>}
        {info && (
          <form onSubmit={submit} data-testid="reset-form">
            <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Choose a new password</h1>
            <p className="mt-3 text-sm text-neutral-600">For <strong data-testid="reset-email">{info.email}</strong>. Any other devices signed in will be signed out.</p>
            <div className="mt-10 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="pw" className="eyebrow">New password</Label>
                <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="rounded-none bg-transparent" data-testid="reset-password-input" />
                <p className="text-xs text-neutral-500">At least 8 characters.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw2" className="eyebrow">Confirm password</Label>
                <Input id="pw2" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className="rounded-none bg-transparent" data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full rounded-none py-6 text-base hover-lift" data-testid="reset-submit-button">{busy ? "One moment…" : "Set new password & sign in"}</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
